import saveAs from "file-saver";
import qs from "qs";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

// The date picker yields UTC midnight of the chosen day; the backend expects "yyyy-MM-dd".
const toIsoDate = (date?: Date | null) =>
  date ? date.toISOString().slice(0, 10) : undefined;


export interface CreateSOAType extends Omit<SOAType, 'profileId'> {
  profileId: string[];
}

export interface SOAType {
  id: string;
  amount: number;
  credit: string;
  debit: string;
  type: string;
  currencyId: number;
  isDeleted: boolean;
  excludeZero?: boolean;
  profileId: string;
  ref: string;
  updatedBy?: string;
  createdDate: string;
}

export async function fetchSOA(
  params: Params<SOAType>
): Promise<IPageable<SOAType>> {
  try {
    const response = await axiosInstance.get("/soas", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the SOA."
    );
  }
}


export const createSOA = async (
  profileIds: string[],
  initialDateSao?: Date | null
) => {
  try {
    const response = await axiosInstance.post("/soas", {
      profileIds,
      initialDateSao: toIsoDate(initialDateSao),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
      "An error occurred while generating the SOA."
    );
  }
};



export const pdfSOASApi = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/soas/${id}/pdf`, {
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = `SOA-${id}.pdf`;
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "application/pdf" });

    saveAs(blob, filename);

    return response.data;
  } catch (error: any) {
    console.error(
      "Error fetching the PDF:",
      error.response?.status,
      error.response?.data
    );
  }
};
export const regenerateSOASApi = async (
  id: string,
  initialDateSao?: Date | null
) => {
  try {
    const response = await axiosInstance.post(`/soas/${id}/regenerate`, null, {
      params: initialDateSao
        ? { initialDateSao: toIsoDate(initialDateSao) }
        : undefined,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while regenerating the SOA."
    );
  }
};


export const useSOAQuery = (
    params: Params<SOAType>
  ): UseQueryResult<IPageable<SOAType>, Error> => {
      const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["soas", JSON.stringify(effectiveParams)];
    return useQuery({
      queryKey,
      queryFn: () => fetchSOA(effectiveParams),
    });
  };
  

  export const sendSingleSoaEmail = async (
    id: string,
    attachCcEmail?: boolean
  ): Promise<any> => {
    try {
      const params: { attachCcEmail?: boolean } = {};
      if (attachCcEmail !== undefined) {
        params.attachCcEmail = attachCcEmail;
      }
      const queryString = qs.stringify(params, { arrayFormat: "comma" });

      const response = await axiosInstance.post(
        `/soas/${id}/send?${queryString}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          `An error occurred while sending email for ID: ${id}`
      );
    }
  };

  export const sendSoaEmails = async (
    ids: string[],
    attachCcEmail?: boolean
  ): Promise<any> => {
    try {
      const body: { ids: number[]; attachCcEmail?: boolean } = {
        ids: ids.map(Number),
      };
      if (attachCcEmail !== undefined) {
        body.attachCcEmail = attachCcEmail;
      }

      const response = await axiosInstance.post("/soas/send", body);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "An error occurred while sending emails."
      );
    }
  };
  

  export async function fetchGeneratedSOAs(params: {
    page: number;
    size: number;
  }): Promise<{
    content: { id: string; ref: string }[];
    totalElements: number;
    totalPages: number;
  }> {
    try {
      const response = await axiosInstance.get("/soas", {
        params: { ...params, status: "GENERATED", excludeZero: true },
      });
      return response.data;
    } catch (error: any) {
      console.error("Error fetching customer bills:", error);
      throw error;
    }
  }